import React from 'react';
import PropTypes from 'prop-types';

import classnames from 'classnames';

import Separator from './separator';
import RangeInput from './range-input';

/**
 * RangeContent component renders a list of RangeInput components with optional separators.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.labelsData - Array of label data objects for each range input.
 * @param {string} props.IdModifier - Unique identifier modifier for the component.
 * @param {Object} props.namespace - Namespace object for CSS class names.
 * @param {string} props.rangeType - Type of the range input.
 * @param {Function} props.handleInputChange - Callback function for input change events.
 * @param {Object} props.handleChangeDropdown - Callback function for dropdown change events.
 * @param {Function} props.handleChangeDropdown.From - Callback function for dropdown change events.
 * @param {Function} props.handleChangeDropdown.To - Callback function for dropdown change events.
 * @param {Function} props.setDropdownItemSelected - Function to set the selected dropdown item.
 * @param {Array} props.filteredValues - Array of filtered values for the range inputs.
 * @param {string} props.onlyNumbersPattern - Regex pattern for allowing only numbers.
 * @param {boolean} [props.isShops=false] - Flag to indicate if the component is for shops.
 * @param {boolean} [props.desktop=false] - Flag to indicate if the component is for desktop view.
 * @returns {JSX.Element} The rendered RangeContent component.
 */

export const RangeContent = ({
  labelsData,
  IdModifier,
  namespace,
  rangeType,
  handleInputChange,
  handleChangeDropdown,
  setDropdownItemSelected,
  filteredValues,
  onlyNumbersPattern,
  isShops = false,
  desktop = false,
}) => (
  <>
    {labelsData.map((value, index) => (
      <React.Fragment key={`${IdModifier}-${value.label}`}>
        <div className={classnames(`${namespace.container}`, { [namespace.shops.container]: isShops })}>
          <RangeInput
            id={IdModifier}
            type={rangeType}
            handleInputChange={handleInputChange}
            handleChangeDropdown={handleChangeDropdown}
            label={value.text}
            name={value.label}
            setDropdownItemSelected={setDropdownItemSelected}
            values={filteredValues}
            pattern={onlyNumbersPattern}
          />
        </div>
        {desktop && index === 0 && <Separator className={namespace.separator} />}
      </React.Fragment>
    ))}
  </>
);

RangeContent.propTypes = {
  desktop: PropTypes.bool,
  filteredValues: PropTypes.array.isRequired,
  handleChangeDropdown: PropTypes.shape({ From: PropTypes.func, To: PropTypes.func }).isRequired,
  handleInputChange: PropTypes.func.isRequired,
  IdModifier: PropTypes.string.isRequired,
  isShops: PropTypes.bool,
  labelsData: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    }),
  ).isRequired,
  namespace: PropTypes.object.isRequired,
  onlyNumbersPattern: PropTypes.string.isRequired,
  rangeType: PropTypes.string.isRequired,
  setDropdownItemSelected: PropTypes.func.isRequired,
};
